import React from "react";
import { Layers, Ruler, Syringe, Activity } from "lucide-react";
import TumorBoardBriefing from "@/components/TumorBoardBriefing";
import DiagnosticsSuggestion from "@/components/DiagnosticsSuggestion";
import {
  ACCENT, STAGE_COLOR, RESPONSE_COLOR, ECOG_COLOR,
  fmtDate, humanize,
} from "./shared";

export default function OverviewTab({ dx, meas, chemo, perf, onJump }) {
  const latestDx = dx.diagnoses[0];
  const s = meas.summary || {};
  const c = chemo.summary || {};
  const latestPerf = perf.records[0];

  return (
    <div className="space-y-4" data-testid="onco-overview">
      <DiagnosticsSuggestion specialty="oncology" />
      {(latestDx || s.latest_response || c.current_regimen) && <TumorBoardBriefing />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <button onClick={() => onJump("tnm")}
              data-testid="onco-overview-dx-card"
              className="text-left ros-card hover:border-[#FBBF24]/40 transition-colors">
        <div className="flex items-center gap-2 ros-label"><Layers size={12} /> Primary diagnosis</div>
        {latestDx ? (
          <>
            <div className="mt-3 text-2xl font-heading font-bold text-white">
              {humanize(latestDx.cancer_type)}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                    data-testid="stage-badge"
                    style={{ color: STAGE_COLOR[latestDx.stage],
                             background: `${STAGE_COLOR[latestDx.stage]}18`,
                             border: `1px solid ${STAGE_COLOR[latestDx.stage]}40` }}>
                STAGE {latestDx.stage}
              </span>
              <span className="text-xs text-[#A3ACA7] font-mono">{latestDx.tnm}</span>
            </div>
            <div className="text-xs text-[#7A8480] mt-3">
              {latestDx.histology || "—"} · Dx {fmtDate(latestDx.date)}
            </div>
          </>
        ) : <div className="mt-4 text-sm text-[#A3ACA7]">No diagnosis recorded yet.</div>}
      </button>

      <button onClick={() => onJump("recist")}
              data-testid="onco-overview-recist-card"
              className="text-left ros-card hover:border-[#FBBF24]/40 transition-colors">
        <div className="flex items-center gap-2 ros-label"><Ruler size={12} /> RECIST 1.1 response</div>
        {s.latest_response ? (
          <>
            <div className="mt-3 flex items-baseline gap-4">
              <span className="text-3xl font-heading font-bold"
                    style={{ color: RESPONSE_COLOR[s.latest_response] || "#fff" }}>
                {s.latest_response}
              </span>
              <span className="text-xs text-[#A3ACA7]">
                SLD {s.latest_sld} mm (baseline {s.baseline_sld} mm)
              </span>
            </div>
            <div className="text-xs text-[#7A8480] mt-3">
              {s.measurement_count} imaging measurements
            </div>
          </>
        ) : <div className="mt-4 text-sm text-[#A3ACA7]">No tumor measurements yet.</div>}
      </button>

      <button onClick={() => onJump("chemo")}
              data-testid="onco-overview-chemo-card"
              className="text-left ros-card hover:border-[#FBBF24]/40 transition-colors">
        <div className="flex items-center gap-2 ros-label"><Syringe size={12} /> Chemotherapy</div>
        {c.current_regimen ? (
          <>
            <div className="mt-3 text-2xl font-heading font-bold text-white">
              {c.current_regimen}
            </div>
            <div className="text-xs text-[#A3ACA7] mt-1">
              {c.current_phase} · {c.current_progress_pct}% complete
            </div>
            <div className="mt-2 h-2 rounded-full bg-[#1C201E] overflow-hidden">
              <div className="h-full transition-all"
                   style={{ width: `${Math.min(100, c.current_progress_pct || 0)}%`, background: ACCENT }} />
            </div>
            <div className="text-xs text-[#7A8480] mt-3">
              Next cycle: {fmtDate(c.next_cycle_date)}
            </div>
          </>
        ) : <div className="mt-4 text-sm text-[#A3ACA7]">No chemotherapy cycles logged.</div>}
      </button>

      <button onClick={() => onJump("performance")}
              data-testid="onco-overview-perf-card"
              className="text-left ros-card hover:border-[#FBBF24]/40 transition-colors">
        <div className="flex items-center gap-2 ros-label"><Activity size={12} /> Performance status</div>
        {latestPerf ? (
          <>
            <div className="mt-3 text-4xl font-heading font-bold" style={{ color: ECOG_COLOR[latestPerf.ecog] }}>
              ECOG {latestPerf.ecog}
            </div>
            <div className="text-xs text-white mt-1">{latestPerf.label}</div>
            <div className="text-[10px] text-[#A3ACA7] mt-2 leading-relaxed">
              {latestPerf.recommendation}
            </div>
          </>
        ) : <div className="mt-4 text-sm text-[#A3ACA7]">No performance-status assessments.</div>}
      </button>
      </div>
    </div>
  );
}
